import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Col,
  FormGroup,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import BankServices from "../../../Services/QRCodeServices/BankServices/BankServices";
import classnames from "classnames";

const AddTransactionModal = ({ isOpen, toggle }) => {
  const [banks, setBanks] = useState([]);
  const [activeTab, setActiveTab] = useState("bankToCounter");

  // Fetch banks for dropdown
  useEffect(() => {
    async function fetchBanks() {
      try {
        const fetchedBanks = await BankServices.getList();
        setBanks(fetchedBanks);
      } catch (err) {
        console.error("Error fetching banks:", err);
        toast.error("Failed to load banks");
      }
    }
    if (isOpen) {
      fetchBanks();
    }
  }, [isOpen]);

  // Validation schema using Yup
  const validationSchema = Yup.object({
    bankId: Yup.number()
      .required("Bank is required")
      .min(1, "Please select a valid bank"),
    amount: Yup.number()
      .required("Amount is required")
      .min(0.01, "Amount must be greater than 0"),
    remarks: Yup.string(),
    valueDate: Yup.date()
      .required("Value Date is required")
      .typeError("Invalid date format"),
  });

  // Formik setup
  const formik = useFormik({
    initialValues: {
      bankId: "",
      amount: "",
      remarks: "",
      valueDate: new Date().toISOString().split("T")[0], // Default to today
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        const payload = {
          bankId: Number(values.bankId),
          amount: Number(values.amount),
          remarks: values.remarks || "",
          valueDate: new Date(values.valueDate).toISOString(),
        };

        if (activeTab === "bankToCounter") {
          await BankServices.addTransaction(payload);
          toast.success("Bank to Counter deposit created successfully!");
        } else {
          await BankServices.addCounterToBankTransaction(payload);
          toast.success("Counter to Bank deposit created successfully!");
        }
        
        toggle();
        formik.resetForm();
      } catch (error) {
        toast.error(error.response?.data || "Failed to create deposit");
        console.error("Error creating deposit:", error);
      }
    },
  });

  const handleCancel = () => {
    formik.resetForm();
    toggle();
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      formik.resetForm();
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={handleCancel}>Add Transaction</ModalHeader>
      <Form onSubmit={formik.handleSubmit}>
        <ModalBody>
          <Nav tabs>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "bankToCounter" })}
                onClick={() => toggleTab("bankToCounter")}
                style={{ cursor: "pointer" }}
              >
                Bank To Counter
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "counterToBank" })}
                onClick={() => toggleTab("counterToBank")}
                style={{ cursor: "pointer" }}
              >
                Counter To Bank
              </NavLink>
            </NavItem>
          </Nav>
          <TabContent activeTab={activeTab}>
            <TabPane tabId="bankToCounter">
              <Row className="mt-3">
                <Col md={12} className="mb-0">
                  <Label for="bankId">Bank</Label>
                  <FormGroup>
                    <Input
                      type="select"
                      id="bankId"
                      name="bankId"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.bankId}
                      invalid={formik.touched.bankId && formik.errors.bankId}
                    >
                      <option value="">Select Bank</option>
                      {banks.map((bank) => (
                        <option key={bank.id} value={bank.id}>
                          {bank.name}
                        </option>
                      ))}
                    </Input>
                    {formik.touched.bankId && formik.errors.bankId && (
                      <FormFeedback>{formik.errors.bankId}</FormFeedback>
                    )}
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col xs={6} className="mb-2">
                  <Label for="amount">Amount</Label>
                  <Input
                    type="number"
                    id="amount"
                    name="amount"
                    placeholder="Enter Amount"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.amount}
                    invalid={formik.touched.amount && formik.errors.amount}
                  />
                  {formik.touched.amount && formik.errors.amount && (
                    <FormFeedback>{formik.errors.amount}</FormFeedback>
                  )}
                </Col>
                <Col xs={6} className="mb-2">
                  <Label for="valueDate">Value Date</Label>
                  <Input
                    type="date"
                    id="valueDate"
                    name="valueDate"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.valueDate}
                    invalid={formik.touched.valueDate && formik.errors.valueDate}
                  />
                  {formik.touched.valueDate && formik.errors.valueDate && (
                    <FormFeedback>{formik.errors.valueDate}</FormFeedback>
                  )}
                </Col>
              </Row>
              <Row>
                <Col md={12} className="mb-3">
                  <Label for="remarks">Remarks</Label>
                  <Input
                    type="textarea"
                    id="remarks"
                    name="remarks"
                    placeholder="Enter Remarks (Optional)"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.remarks}
                    invalid={formik.touched.remarks && formik.errors.remarks}
                  />
                  {formik.touched.remarks && formik.errors.remarks && (
                    <FormFeedback>{formik.errors.remarks}</FormFeedback>
                  )}
                </Col>
              </Row>
            </TabPane>
            <TabPane tabId="counterToBank">
              <Row className="mt-3">
                <Col md={12} className="mb-0">
                  <Label for="bankId">Bank</Label>
                  <FormGroup>
                    <Input
                      type="select"
                      id="bankId"
                      name="bankId"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.bankId}
                      invalid={formik.touched.bankId && formik.errors.bankId}
                    >
                      <option value="">Select Bank</option>
                      {banks.map((bank) => (
                        <option key={bank.id} value={bank.id}>
                          {bank.name}
                        </option>
                      ))}
                    </Input>
                    {formik.touched.bankId && formik.errors.bankId && (
                      <FormFeedback>{formik.errors.bankId}</FormFeedback>
                    )}
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col xs={6} className="mb-2">
                  <Label for="amount">Amount</Label>
                  <Input
                    type="number"
                    id="amount"
                    name="amount"
                    placeholder="Enter Amount"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.amount}
                    invalid={formik.touched.amount && formik.errors.amount}
                  />
                  {formik.touched.amount && formik.errors.amount && (
                    <FormFeedback>{formik.errors.amount}</FormFeedback>
                  )}
                </Col>
                <Col xs={6} className="mb-2">
                  <Label for="valueDate">Value Date</Label>
                  <Input
                    type="date"
                    id="valueDate"
                    name="valueDate"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.valueDate}
                    invalid={formik.touched.valueDate && formik.errors.valueDate}
                  />
                  {formik.touched.valueDate && formik.errors.valueDate && (
                    <FormFeedback>{formik.errors.valueDate}</FormFeedback>
                  )}
                </Col>
              </Row>
              <Row>
                <Col md={12} className="mb-3">
                  <Label for="remarks">Remarks</Label>
                  <Input
                    type="textarea"
                    id="remarks"
                    name="remarks"
                    placeholder="Enter Remarks (Optional)"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.remarks}
                    invalid={formik.touched.remarks && formik.errors.remarks}
                  />
                  {formik.touched.remarks && formik.errors.remarks && (
                    <FormFeedback>{formik.errors.remarks}</FormFeedback>
                  )}
                </Col>
              </Row>
            </TabPane>
          </TabContent>
        </ModalBody>
        <ModalFooter>
          <Button type="submit" color="success" disabled={formik.isSubmitting}>
            Save
          </Button>
          <Button color="danger" onClick={handleCancel} type="button">
            Cancel
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddTransactionModal;